// Patient repository: encrypts identity into the envelope, maintains blind
// indexes and the local-only decrypted search table (SECURITY.md §3).
import type { AbstractPowerSyncDatabase } from '@powersync/web';
import type { ClinicCrypto } from '@/lib/crypto/clinic-crypto';
import { newId, nowIso } from '@/lib/db/db';
import type { PatientIndexRow } from '@/lib/db/schema';
import { normalizePhone } from '@/lib/myanmar/phone';
import { canonicalNrc, parseNrc } from '@/lib/myanmar/nrc';
import { normalizeName, toUnicode } from '@/lib/myanmar/zawgyi';
import { toAsciiDigits } from '@/lib/myanmar/digits';

export interface PatientIdentity {
  honorific?: string | null;
  name_my?: string | null;
  name_en?: string | null;
  dob?: string | null;            // YYYY-MM-DD
  age_years?: number | null;
  age_months?: number | null;
  age_recorded_on?: string | null;
  nrc?: string | null;            // canonical or free text
  address_line?: string | null;
  notes?: string | null;
}

export interface NewPatient extends PatientIdentity {
  sex: 'male' | 'female' | 'other' | 'unknown';
  age_approximate?: boolean;
  blood_group?: string | null;
  address_pcode?: string | null;
  phones: { phone: string; label?: string; viber?: boolean }[];
  consent_messaging?: boolean;
  allergies?: { substance: string; generic_name?: string | null; reaction?: string | null; severity?: string | null }[];
}

export interface Ctx { db: AbstractPowerSyncDatabase; crypto: ClinicCrypto; clinicId: string; deviceId: string | null; userId: string | null }

function birthYear(p: PatientIdentity): number | null {
  if (p.dob) return Number(p.dob.slice(0, 4));
  if (p.age_years != null && p.age_recorded_on) return Number(p.age_recorded_on.slice(0, 4)) - p.age_years;
  return null;
}

export function ageLabel(row: { dob: string | null; age_years: number | null; age_months: number | null; age_recorded_on: string | null }): { years: number; months: number } | null {
  const today = new Date();
  if (row.dob) {
    const d = new Date(row.dob);
    let years = today.getFullYear() - d.getFullYear();
    let months = today.getMonth() - d.getMonth();
    if (today.getDate() < d.getDate()) months -= 1;
    if (months < 0) { years -= 1; months += 12; }
    return { years, months };
  }
  if (row.age_years != null && row.age_recorded_on) {
    const rec = new Date(row.age_recorded_on);
    const elapsed = (today.getFullYear() - rec.getFullYear()) * 12 + (today.getMonth() - rec.getMonth());
    const total = row.age_years * 12 + (row.age_months ?? 0) + elapsed;
    return { years: Math.floor(total / 12), months: total % 12 };
  }
  return null;
}

/** Next patient number from this device's reserved block, or null to let the server assign. */
async function nextPatientNo(ctx: Ctx, prefix: string): Promise<string | null> {
  const row = await ctx.db.getOptional<{ id: string; next_value: number; range_end: number }>(
    'select id, next_value, range_end from local_numbers where clinic_id = ? and kind = ? and next_value <= range_end', [ctx.clinicId, 'patient_no']);
  if (!row) return null;
  await ctx.db.execute('update local_numbers set next_value = next_value + 1 where id = ?', [row.id]);
  return `${prefix}-${String(row.next_value).padStart(6, '0')}`;
}

export async function registerPatient(ctx: Ctx, input: NewPatient, prefix: string): Promise<{ id: string; patient_no: string | null }> {
  const id = newId();
  const ts = nowIso();
  const nameMy = input.name_my ? toUnicode(input.name_my).text : null;
  const nrcParsed = input.nrc ? parseNrc(input.nrc) : null;
  const nrcCanonical = nrcParsed ? canonicalNrc(nrcParsed) : (input.nrc?.trim() || null);
  const identity: PatientIdentity = {
    honorific: input.honorific ?? null, name_my: nameMy, name_en: input.name_en?.trim() || null,
    dob: input.dob ?? null, age_years: input.age_years ?? null, age_months: input.age_months ?? null,
    age_recorded_on: input.age_recorded_on ?? (input.age_years != null ? ts.slice(0, 10) : null),
    nrc: nrcCanonical, address_line: input.address_line ?? null, notes: input.notes ?? null,
  };
  const enc = await ctx.crypto.encrypt(id, identity as Record<string, unknown>);
  const nameForIndex = identity.name_en ?? identity.name_my;
  const nameHmac = nameForIndex ? await ctx.crypto.index('name', normalizeName(nameForIndex)) : null;
  const nrcHmac = nrcCanonical ? await ctx.crypto.index('nrc', nrcCanonical) : null;
  const patientNo = await nextPatientNo(ctx, prefix);

  const phones: { id: string; e164: string; enc: string; hmac: string; label: string | null; viber: boolean; primary: boolean }[] = [];
  for (const [i, p] of input.phones.entries()) {
    const e164 = normalizePhone(p.phone);
    if (!e164) continue;
    const pid = newId();
    phones.push({ id: pid, e164, enc: await ctx.crypto.encrypt(pid, { phone_e164: e164 }), hmac: await ctx.crypto.index('phone', e164), label: p.label ?? 'mobile', viber: !!p.viber, primary: i === 0 });
  }
  const allergies: { id: string; enc: string; generic: string | null; severity: string | null }[] = [];
  for (const a of input.allergies ?? []) {
    const aid = newId();
    allergies.push({ id: aid, enc: await ctx.crypto.encrypt(aid, { substance: a.substance, reaction: a.reaction ?? null }), generic: a.generic_name ?? null, severity: a.severity ?? null });
  }

  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute(
      `insert into patients (id, clinic_id, patient_no, enc, key_version, sex, birth_year, age_approximate, blood_group, address_pcode, nrc_hmac, name_hmac, consent_messaging, created_at, created_by, updated_at, updated_by, device_id)
       values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [id, ctx.clinicId, patientNo, enc, ctx.crypto.active.version, input.sex, birthYear(identity), input.age_approximate ? 1 : 0, input.blood_group ?? null, input.address_pcode ?? null, nrcHmac, nameHmac, input.consent_messaging ? 1 : 0, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    for (const p of phones) {
      await tx.execute(
        `insert into patient_phones (id, clinic_id, patient_id, enc, phone_hmac, label, is_primary, viber, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [p.id, ctx.clinicId, id, p.enc, p.hmac, p.label, p.primary ? 1 : 0, p.viber ? 1 : 0, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
    for (const a of allergies) {
      await tx.execute(
        `insert into patient_allergies (id, clinic_id, patient_id, enc, generic_name, severity, noted_on, active, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?)`,
        [a.id, ctx.clinicId, id, a.enc, a.generic, a.severity, ts.slice(0, 10), ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
    await tx.execute(
      `insert into patient_index (id, clinic_id, patient_id, patient_no, name_my, name_en, name_search, phones, nrc, dob, age_years, age_months, age_recorded_on, honorific, updated_at)
       values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [newId(), ctx.clinicId, id, patientNo, identity.name_my, identity.name_en, searchText(identity, phones.map((p) => p.e164)), JSON.stringify(phones.map((p) => p.e164)), nrcCanonical, identity.dob, identity.age_years, identity.age_months, identity.age_recorded_on, identity.honorific, ts]);
  });
  return { id, patient_no: patientNo };
}

function searchText(identity: PatientIdentity, phones: string[]): string {
  return [identity.name_en && normalizeName(identity.name_en), identity.name_my && normalizeName(identity.name_my), identity.nrc, ...phones, ...phones.map((p) => '0' + p.slice(3))]
    .filter(Boolean).join(' ').toLowerCase();
}

/** Local search: phone, name (either script), patient number, NRC. */
export async function searchPatients(db: AbstractPowerSyncDatabase, clinicId: string, query: string, limit = 20): Promise<PatientIndexRow[]> {
  const q = toAsciiDigits(query).trim().toLowerCase();
  if (!q) {
    return db.getAll<PatientIndexRow>('select * from patient_index where clinic_id = ? order by updated_at desc limit ?', [clinicId, limit]);
  }
  const like = `%${normalizeName(q)}%`;
  return db.getAll<PatientIndexRow>(
    `select * from patient_index where clinic_id = ? and (name_search like ? or patient_no like ? or phones like ?) order by updated_at desc limit ?`,
    [clinicId, like, `%${q.toUpperCase()}%`, `%${q.replace(/^0/, '')}%`, limit]);
}

export async function getPatientIndex(db: AbstractPowerSyncDatabase, patientId: string): Promise<PatientIndexRow | null> {
  return db.getOptional<PatientIndexRow>('select * from patient_index where patient_id = ?', [patientId]);
}

/** Rebuild the local index from synced envelopes (after unlock on a new device, or after sync). */
export async function rebuildPatientIndex(ctx: Ctx): Promise<number> {
  const rows = await ctx.db.getAll<{ id: string; patient_no: string | null; enc: string; updated_at: string }>(
    `select p.id, p.patient_no, p.enc, p.updated_at from patients p left join patient_index i on i.patient_id = p.id
     where p.clinic_id = ? and p.merged_into is null
       and (i.id is null or i.updated_at < p.updated_at or i.patient_no is distinct from p.patient_no)`, [ctx.clinicId]);
  let failed = 0;
  for (const r of rows) {
    let identity: PatientIdentity;
    try { identity = await ctx.crypto.decrypt<PatientIdentity>(r.id, r.enc); }
    catch { failed++; continue; }   // encrypted with a key this device does not hold yet
    const phoneRows = await ctx.db.getAll<{ id: string; enc: string }>('select id, enc from patient_phones where patient_id = ?', [r.id]);
    const phones: string[] = [];
    for (const pr of phoneRows) { try { phones.push((await ctx.crypto.decrypt<{ phone_e164: string }>(pr.id, pr.enc)).phone_e164); } catch { /* skip */ } }
    await ctx.db.execute('delete from patient_index where patient_id = ?', [r.id]);
    await ctx.db.execute(
      `insert into patient_index (id, clinic_id, patient_id, patient_no, name_my, name_en, name_search, phones, nrc, dob, age_years, age_months, age_recorded_on, honorific, updated_at) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [newId(), ctx.clinicId, r.id, r.patient_no, identity.name_my ?? null, identity.name_en ?? null, searchText(identity, phones), JSON.stringify(phones), identity.nrc ?? null, identity.dob ?? null, identity.age_years ?? null, identity.age_months ?? null, identity.age_recorded_on ?? null, identity.honorific ?? null, r.updated_at]);
  }
  if (failed) console.warn(`[index] ${failed} patient row(s) could not be decrypted with the keys on this device`);
  return rows.length - failed;
}

export interface PatientForEdit extends NewPatient { id: string; patient_no: string | null }

/** Decrypt everything the edit form needs. */
export async function loadPatientForEdit(ctx: Ctx, patientId: string): Promise<PatientForEdit | null> {
  const row = await ctx.db.getOptional<{ id: string; patient_no: string | null; enc: string; sex: NewPatient['sex']; age_approximate: number; blood_group: string | null; address_pcode: string | null; consent_messaging: number }>(
    'select id, patient_no, enc, sex, age_approximate, blood_group, address_pcode, consent_messaging from patients where id = ?', [patientId]);
  if (!row) return null;
  const identity = await ctx.crypto.decrypt<PatientIdentity>(row.id, row.enc);
  const phoneRows = await ctx.db.getAll<{ id: string; enc: string; label: string | null; viber: number }>('select id, enc, label, viber from patient_phones where patient_id = ? order by is_primary desc', [patientId]);
  const phones: NewPatient['phones'] = [];
  for (const p of phoneRows) { try { phones.push({ phone: (await ctx.crypto.decrypt<{ phone_e164: string }>(p.id, p.enc)).phone_e164, label: p.label ?? undefined, viber: p.viber === 1 }); } catch { /* skip */ } }
  const allergyRows = await ctx.db.getAll<{ id: string; enc: string; generic_name: string | null; severity: string | null }>('select id, enc, generic_name, severity from patient_allergies where patient_id = ? and active = 1', [patientId]);
  const allergies: NonNullable<NewPatient['allergies']> = [];
  for (const a of allergyRows) { try { const d = await ctx.crypto.decrypt<{ substance: string; reaction: string | null }>(a.id, a.enc); allergies.push({ substance: d.substance, reaction: d.reaction, generic_name: a.generic_name, severity: a.severity }); } catch { /* skip */ } }
  return { id: row.id, patient_no: row.patient_no, ...identity, sex: row.sex, age_approximate: row.age_approximate === 1, blood_group: row.blood_group, address_pcode: row.address_pcode, consent_messaging: row.consent_messaging === 1, phones, allergies };
}

/** Re-encrypt and replace a patient's details. Phones and allergies are replaced as a set. */
export async function updatePatient(ctx: Ctx, patientId: string, input: NewPatient): Promise<void> {
  const ts = nowIso();
  const current = await ctx.db.getOptional<{ patient_no: string | null }>('select patient_no from patients where id = ?', [patientId]);
  const nameMy = input.name_my ? toUnicode(input.name_my).text : null;
  const nrcParsed = input.nrc ? parseNrc(input.nrc) : null;
  const nrcCanonical = nrcParsed ? canonicalNrc(nrcParsed) : (input.nrc?.trim() || null);
  const identity: PatientIdentity = {
    honorific: input.honorific ?? null, name_my: nameMy, name_en: input.name_en?.trim() || null,
    dob: input.dob ?? null, age_years: input.age_years ?? null, age_months: input.age_months ?? null,
    age_recorded_on: input.age_recorded_on ?? (input.age_years != null ? ts.slice(0, 10) : null),
    nrc: nrcCanonical, address_line: input.address_line ?? null, notes: input.notes ?? null,
  };
  const enc = await ctx.crypto.encrypt(patientId, identity as Record<string, unknown>);
  const nameForIndex = identity.name_en ?? identity.name_my;
  const nameHmac = nameForIndex ? await ctx.crypto.index('name', normalizeName(nameForIndex)) : null;
  const nrcHmac = nrcCanonical ? await ctx.crypto.index('nrc', nrcCanonical) : null;
  const phones: { id: string; e164: string; enc: string; hmac: string; label: string | null; viber: boolean; primary: boolean }[] = [];
  for (const [i, p] of input.phones.entries()) {
    const e164 = normalizePhone(p.phone); if (!e164) continue;
    const pid = newId();
    phones.push({ id: pid, e164, enc: await ctx.crypto.encrypt(pid, { phone_e164: e164 }), hmac: await ctx.crypto.index('phone', e164), label: p.label ?? 'mobile', viber: !!p.viber, primary: i === 0 });
  }
  const allergies: { id: string; enc: string; generic: string | null; severity: string | null }[] = [];
  for (const a of input.allergies ?? []) {
    const aid = newId();
    allergies.push({ id: aid, enc: await ctx.crypto.encrypt(aid, { substance: a.substance, reaction: a.reaction ?? null }), generic: a.generic_name ?? null, severity: a.severity ?? null });
  }
  await ctx.db.writeTransaction(async (tx) => {
    await tx.execute(
      `update patients set enc = ?, key_version = ?, sex = ?, birth_year = ?, age_approximate = ?, blood_group = ?, address_pcode = ?, nrc_hmac = ?, name_hmac = ?, consent_messaging = ?, updated_at = ?, updated_by = ?, device_id = ? where id = ?`,
      [enc, ctx.crypto.active.version, input.sex, birthYear(identity), input.age_approximate ? 1 : 0, input.blood_group ?? null, input.address_pcode ?? null, nrcHmac, nameHmac, input.consent_messaging ? 1 : 0, ts, ctx.userId, ctx.deviceId, patientId]);
    await tx.execute('delete from patient_phones where patient_id = ?', [patientId]);
    for (const p of phones) {
      await tx.execute(`insert into patient_phones (id, clinic_id, patient_id, enc, phone_hmac, label, is_primary, viber, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [p.id, ctx.clinicId, patientId, p.enc, p.hmac, p.label, p.primary ? 1 : 0, p.viber ? 1 : 0, ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
    await tx.execute('update patient_allergies set active = 0, updated_at = ?, updated_by = ? where patient_id = ? and active = 1', [ts, ctx.userId, patientId]);
    for (const a of allergies) {
      await tx.execute(`insert into patient_allergies (id, clinic_id, patient_id, enc, generic_name, severity, noted_on, active, created_at, created_by, updated_at, updated_by, device_id) values (?, ?, ?, ?, ?, ?, ?, 1, ?, ?, ?, ?, ?)`,
        [a.id, ctx.clinicId, patientId, a.enc, a.generic, a.severity, ts.slice(0, 10), ts, ctx.userId, ts, ctx.userId, ctx.deviceId]);
    }
    await tx.execute('delete from patient_index where patient_id = ?', [patientId]);
    await tx.execute(
      `insert into patient_index (id, clinic_id, patient_id, patient_no, name_my, name_en, name_search, phones, nrc, dob, age_years, age_months, age_recorded_on, honorific, updated_at) values (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [newId(), ctx.clinicId, patientId, current?.patient_no ?? null, identity.name_my, identity.name_en, searchText(identity, phones.map((p) => p.e164)), JSON.stringify(phones.map((p) => p.e164)), nrcCanonical, identity.dob, identity.age_years, identity.age_months, identity.age_recorded_on, identity.honorific, ts]);
  });
}
